import { Reveal } from "@/components/bits";
import PreorderCta from "@/components/store/PreorderCta";

// Why POSSO — the most visual section on the landing page. Each benefit:
// large visual → big headline → ONE short sentence. Alternating colour
// pills, no identical feature cards.
const BENEFITS = [
  {
    pill: "GRIP",
    pillBg: "#7CC9FF",
    headline: "Slip-resistant grip.",
    sentence: "An outsole designed for wet, unpredictable workplace floors.",
    image: { src: "/images/feature-grip.webp", alt: "Close-up of the POSSO slip-resistant outsole tread" },
  },
  {
    pill: "ANATOMY",
    pillBg: "#FF6BAA",
    headline: "Every layer does a job.",
    sentence: "Reinforced toe cap, gel arch insole, slip-resistant outsole — in one lightweight build.",
    image: { src: "/images/anatomy.webp", alt: "Cutaway of the pink POSSO clog showing its reinforced toe cap, gel arch insole and slip-resistant outsole" },
  },
  {
    pill: "COMFORT",
    pillBg: "#FFD84D",
    headline: "Made with long shifts in mind.",
    sentence: "Lightweight, cushioned and built around hours on your feet.",
    image: { src: "/images/proof-onfoot.webp", alt: "Nurse in scrubs wearing yellow POSSO clogs in a hospital corridor" },
  },
  {
    pill: "EASY CLEAN",
    pillBg: "#C6A2FF",
    headline: "Because work gets messy.",
    sentence: "A closed upper that wipes down in seconds.",
    image: { src: "/images/feature-clean.webp", alt: "Water beading off the closed upper of a light blue POSSO clog" },
  },
];

export default function WhyPogo() {
  return (
    <section id="why" data-testid="why-pogo" className="bg-white">
      <div className="px-4 py-12 sm:py-16 max-w-2xl mx-auto w-full">
        <Reveal>
          <p className="font-body font-bold text-[11px] tracking-[0.22em] uppercase text-ink/50">
            WHY POSSO
          </p>
          <h2 className="mt-3 font-display uppercase leading-[0.95] text-[8vw] sm:text-4xl">
            5 reasons Aussie healthcare workers choose{" "}
            <span className="text-[#FF6BAA]">POSSO</span>
          </h2>
        </Reveal>

        <div className="mt-9 space-y-10">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.pill} delay={0.03}>
              <div data-testid={`benefit-${i + 1}`}>
                <img
                  src={b.image.src}
                  alt={b.image.alt}
                  className="w-full aspect-[4/3] object-cover rounded-3xl border-2 border-ink shadow-[4px_4px_0px_#111111] select-none"
                  loading="lazy"
                  draggable="false"
                />
                <span
                  className="mt-4 inline-block font-display uppercase text-xs tracking-wide border-2 border-ink rounded-full px-3.5 py-1.5"
                  style={{ backgroundColor: b.pillBg }}
                >
                  {b.pill}
                </span>
                <h3 className="mt-3 font-display uppercase leading-[0.95] text-[8.5vw] sm:text-4xl">
                  {b.headline}
                </h3>
                <p className="mt-2 text-base sm:text-lg text-ink/70 max-w-md">
                  {b.sentence}
                </p>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.03}>
            <div data-testid="benefit-5">
              <img
                src="/images/insole.webp?v=2"
                alt="POSSO arch-support insole — honeycomb-textured top with pink sculpted arch pad and heel cup"
                className="w-full aspect-[4/3] object-cover rounded-3xl border-2 border-ink shadow-[4px_4px_0px_#111111] select-none"
                loading="lazy"
                draggable="false"
              />
              <span className="mt-4 inline-block font-display uppercase text-xs tracking-wide border-2 border-ink rounded-full px-3.5 py-1.5 bg-[#FF6BAA]">
                INSIDE EVERY PAIR
              </span>
              <h3 className="mt-3 font-display uppercase leading-[0.95] text-[8.5vw] sm:text-4xl">
                Comfort you can see.
              </h3>
              <p className="mt-2 text-base sm:text-lg text-ink/70 max-w-md">
                Every pair ships with our arch-support insole — sculpted
                arch pad and a deep heel cup, topped with the signature
                honeycomb texture.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal>
          <PreorderCta
            testId="benefits-cta"
            origin="landing_benefits"
            label="Pick your colour"
            className="mt-6 w-full sm:max-w-sm"
          />
        </Reveal>
      </div>
    </section>
  );
}
